import { ChangeEvent, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { BetbyBonusService, BetbyDbTemplate, GiveBonusResult } from "services/betbyBonus/betbyBonus.service"
import { settingsService } from "services/settings/settings.service"
import { SingleUserTableModel } from "services/user/type"
import { userService } from "services/user/user.service"

const FALLBACK_CURRENCY_OPTIONS = ["USD", "EUR", "CAD"]

// ─── User search picker ───────────────────────────────────────────────────────

interface UserPickerProps {
  selectedUsers: SingleUserTableModel[]
  onAdd: (user: SingleUserTableModel) => void
  onRemove: (id: string) => void
}

function UserPicker({ selectedUsers, onAdd, onRemove }: UserPickerProps) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SingleUserTableModel[]>([])
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const search = (q: string) => {
    setQuery(q)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!q.trim()) {
      setResults([])
      return
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      const res = await userService.getUsersTable(1, 10, undefined, { q, createdAt: [], country: null })
      setResults(res?.data ?? [])
      setSearching(false)
    }, 300)
  }

  const selectedIds = new Set(selectedUsers.map((u) => u.id))

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search & add players</label>
      <div className="relative">
        <input
          type="text"
          className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          placeholder="Search by username or email…"
          value={query}
          onChange={(e) => search(e.target.value)}
          autoComplete="new-password"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />
        {(results.length > 0 || searching) && query && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded shadow-lg max-h-48 overflow-y-auto">
            {searching && <div className="px-3 py-2 text-xs text-gray-400">Searching…</div>}
            {results.map((u) => (
              <button
                key={u.id}
                type="button"
                disabled={selectedIds.has(u.id)}
                onClick={() => {
                  onAdd(u)
                  setQuery("")
                  setResults([])
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between disabled:opacity-40"
              >
                <span>
                  <span className="font-medium text-gray-900 dark:text-white">{u.username}</span>
                  <span className="ml-2 text-gray-400 text-xs">{u.email_address}</span>
                </span>
                {selectedIds.has(u.id) && <span className="text-xs text-gray-400">added</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedUsers.length > 0 && (
        <div className="mt-2 flex flex-col gap-1">
          {selectedUsers.map((u) => (
            <div key={u.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded px-3 py-1.5 text-sm">
              <span>
                <span className="font-medium text-gray-900 dark:text-white">{u.username}</span>
                <span className="ml-2 text-gray-400 text-xs font-mono">{u.id}</span>
              </span>
              <button onClick={() => onRemove(u.id)} className="text-red-400 hover:text-red-600 text-xs ml-2">
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface Props {
  template: BetbyDbTemplate
}

const STATUS_CLASSES: Record<string, string> = {
  ok: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100",
  error: "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100"
}

export function IssueBonusForm({ template }: Props) {
  // ── Amount logic ─────────────────────────────────────────────────────────
  // is_api_amount: true  → we must supply amount per-issue (show field)
  // is_api_amount: false → amount is fixed in BetBy template (hide field)
  // comboboost           → no monetary amount (hide field)
  const isApiAmount = template.freebet_data?.is_api_amount ?? false
  const isComboboost = template.type === "comboboost"
  const showAmountField = !isComboboost && isApiAmount

  const defaultAmount = template.default_amount_cents ?? template.freebet_data?.amount_list?.[0] ?? 500
  const defaultCurrency = template.default_currency ?? "USD"

  const [selectedUsers, setSelectedUsers] = useState<SingleUserTableModel[]>([])
  const [userIdsText, setUserIdsText] = useState("")
  const [amountCents, setAmountCents] = useState<number>(defaultAmount)
  const [currency, setCurrency] = useState<string>(defaultCurrency)
  const [forceActivated, setForceActivated] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [results, setResults] = useState<GiveBonusResult[]>([])
  const [availableCurrencies, setAvailableCurrencies] = useState<string[]>(FALLBACK_CURRENCY_OPTIONS)
  const fileRef = useRef<HTMLInputElement>(null)

  // Fetch enabled currencies from settings
  useEffect(() => {
    settingsService.getAll().then((settings) => {
      if (settings?.rcoin) {
        const enabled = Object.entries(settings.rcoin)
          .filter(([, on]) => on)
          .map(([coin]) => coin)
          .sort()
        if (enabled.length > 0) setAvailableCurrencies(enabled)
      }
    })
  }, [])

  // Re-sync all defaults when template changes
  useEffect(() => {
    setAmountCents(defaultAmount)
    setCurrency(defaultCurrency)
    setResults([])
    setUserIdsText("")
    setSelectedUsers([])
  }, [template.id]) // eslint-disable-line

  // Merge selected users + manually typed IDs
  const allUserIds = (): string[] => {
    const fromPicker = selectedUsers.map((u) => u.id)
    const fromText = userIdsText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    return Array.from(new Set([...fromPicker, ...fromText]))
  }

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      setUserIdsText(ev.target?.result as string)
    }
    reader.readAsText(file)
    e.target.value = ""
  }

  const handleSubmit = async () => {
    const userIds = allUserIds()
    if (userIds.length === 0) {
      toast.error("Add at least one player")
      return
    }
    if (showAmountField && (!amountCents || amountCents <= 0)) {
      toast.error("Amount must be greater than 0")
      return
    }
    setSubmitting(true)
    try {
      const res = await BetbyBonusService.giveBonus({
        templateId: template.betby_id,
        players: userIds.map((userId) => ({
          userId,
          currency,
          amountCents: showAmountField ? amountCents : 0,
          forceActivated
        }))
      })
      setResults(res)
      const ok = res.filter((r) => r.status === "ok").length
      const err = res.filter((r) => r.status === "error").length
      if (res.length === 0) {
        toast.error("No response from server — check network or API logs")
      } else {
        if (ok > 0) toast.success(`${ok} bonus${ok === 1 ? "" : "es"} issued successfully`)
        if (err > 0) toast.error(`${err} error${err === 1 ? "" : "s"} — check results below`)
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to issue bonus")
    } finally {
      setSubmitting(false)
    }
  }

  const totalPlayers = allUserIds().length

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Template info */}
      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded text-sm text-blue-800 dark:text-blue-200 flex flex-wrap gap-2 items-center">
        <span>
          Template: <strong>{template.name}</strong> ({template.type})
        </span>
        {isComboboost && (
          <span className="bg-orange-100 text-orange-700 dark:bg-orange-800 dark:text-orange-100 px-2 py-0.5 rounded text-xs font-medium">
            combo boost — no monetary amount
          </span>
        )}
        {!isComboboost && !isApiAmount && template.freebet_data?.amount_list && (
          <span className="bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-100 px-2 py-0.5 rounded text-xs font-medium">
            fixed amounts: {template.freebet_data.amount_list.map((c) => `$${(c / 100).toFixed(2)}`).join(", ")}
          </span>
        )}
        {showAmountField && (
          <span className="bg-yellow-100 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-100 px-2 py-0.5 rounded text-xs font-medium">
            amount set per-issue
          </span>
        )}
      </div>

      {/* User picker */}
      <UserPicker
        selectedUsers={selectedUsers}
        onAdd={(u) => setSelectedUsers((prev) => [...prev, u])}
        onRemove={(id) => setSelectedUsers((prev) => prev.filter((u) => u.id !== id))}
      />

      {/* Manual textarea fallback */}
      <details className="text-sm">
        <summary className="cursor-pointer text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 select-none">
          Or paste / upload user IDs manually
        </summary>
        <div className="mt-2">
          <div className="flex justify-end mb-1">
            <button type="button" onClick={() => fileRef.current?.click()} className="text-xs text-blue-500 hover:text-blue-700">
              Upload CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFileUpload} />
          </div>
          <textarea
            className="w-full h-24 px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono"
            placeholder={"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx\nxxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"}
            value={userIdsText}
            onChange={(e) => setUserIdsText(e.target.value)}
          />
        </div>
      </details>

      {/* Amount + Currency — only shown when is_api_amount=true */}
      {showAmountField && (
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Amount{" "}
              <span className="text-gray-400 font-normal text-xs">
                (in cents — {amountCents > 0 ? `$${(amountCents / 100).toFixed(2)}` : "enter value"})
              </span>
            </label>
            <input
              type="number"
              min={1}
              className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={amountCents}
              onChange={(e) => setAmountCents(parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
            {template.default_currency ? (
              <div className="px-3 py-2 text-sm border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 text-gray-500 dark:text-gray-400 select-none">
                {currency}
                <span className="ml-1 text-xs text-gray-400">(set by template)</span>
              </div>
            ) : (
              <select
                className="px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {availableCurrencies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* Force activate */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <input
            id="forceActivate"
            type="checkbox"
            className="rounded"
            checked={forceActivated}
            onChange={(e) => setForceActivated(e.target.checked)}
          />
          <label htmlFor="forceActivate" className="text-sm text-gray-700 dark:text-gray-300">
            Force activate immediately
            <span className="ml-1 text-gray-400 text-xs">(skip player click in My Bets)</span>
          </label>
        </div>
        {forceActivated && (
          <p className="text-xs text-yellow-600 dark:text-yellow-400 ml-6">
            ⚠️ Force-activated freebets are <strong>not shown</strong> on the user&apos;s My Bonuses page — they appear only inside the
            BetBy sportsbook widget. The player won&apos;t see a notification.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || totalPlayers === 0}
        className={clsx("btn btn-success text-white", submitting && "loading")}
      >
        {!submitting && `Issue to ${totalPlayers || "…"} player(s)`}
      </button>

      {/* Results */}
      {results.length > 0 && (
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Results: {results.filter((r) => r.status === "ok").length} issued, {results.filter((r) => r.status === "error").length} errors
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b dark:border-gray-700 text-left text-gray-500 uppercase">
                  <th className="py-1.5 pr-4">User</th>
                  <th className="py-1.5 pr-4">Status</th>
                  <th className="py-1.5 pr-4">BetBy Bonus ID</th>
                  <th className="py-1.5">Error</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => {
                  const user = selectedUsers.find((u) => u.id === r.userId)
                  return (
                    <tr key={i} className="border-b dark:border-gray-700">
                      <td className="py-1.5 pr-4 font-mono text-gray-700 dark:text-gray-300">
                        {user ? (
                          <span>
                            {user.username} <span className="text-gray-400">{r.userId}</span>
                          </span>
                        ) : (
                          r.userId
                        )}
                      </td>
                      <td className="py-1.5 pr-4">
                        <span className={clsx("px-2 py-0.5 rounded-full text-xs font-medium", STATUS_CLASSES[r.status] ?? "")}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-1.5 pr-4 font-mono text-gray-500">{r.betbyBonusId ?? "—"}</td>
                      <td className="py-1.5 text-red-600 dark:text-red-400">{r.error ?? "—"}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
